import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  type Color,
} from "@salestrak/ui";

import type { StageOption } from "#app/lib/data";
import { StageGroupChip } from "#app/components/issues/issue-group-title";
import { StageInlineEditor } from "#app/components/stages/stage-inline-editor";

type StageColumnHeaderProps = {
  stage: StageOption;
  count: number;
  /** Viewer holds `manage.edit_workflows`. */
  canEditStage: boolean;
  onRename: (stageId: string, name: string) => void;
  onRecolor: (stage: StageOption, color: Color) => void;
  /** Deep-link to the workflow's Stages settings, from the palette's footer. */
  onOpenSettings?: (() => void) | undefined;
  /** The column's takeover switch — the edit row IS the header while it is up. */
  setControlsHidden: (hidden: boolean) => void;
};

/**
 * A stage column's header, with inline rename and recolor. At rest it reads
 * [chip · count], the chip being the same housed neutral StageGroupChip the
 * list's group band wears — grey wash, hue only in the pie — so a stage heads
 * a column exactly the way it heads a group. An editor pressing the chip swaps
 * in the shared StageInlineEditor. A viewer without `manage.edit_workflows`
 * gets the identical resting chrome with no press affordance.
 */
export function StageColumnHeader({
  stage,
  count,
  canEditStage,
  onRename,
  onRecolor,
  onOpenSettings,
  setControlsHidden,
}: StageColumnHeaderProps) {
  const [editing, setEditing] = useState(false);

  // The edit row takes the full header width: the column drops its collapse /
  // ⋯ / add cluster while it is up, and gets it back on exit or unmount (a
  // grouping switch mid-edit unmounts the column).
  useEffect(() => {
    setControlsHidden(editing);
    return () => setControlsHidden(false);
  }, [editing, setControlsHidden]);

  const exitEditing = useCallback(() => {
    setEditing(false);
  }, []);

  if (editing) {
    return (
      <StageInlineEditor
        stage={stage}
        onRename={onRename}
        onRecolor={onRecolor}
        onOpenSettings={onOpenSettings}
        onExit={exitEditing}
      />
    );
  }

  // The chip insets its pie 8px (7px padding + the Badge's 1px border); -8px
  // puts the pie on the header's 16px glyph line (header inset 8 + this row's
  // 8px lead-in), where the "+ Add Project" footer's plus and every other
  // dimension's bare glyph sit. It overrides the chip's list-band pull, which
  // is tuned to the table's row pies.
  const chip = canEditStage ? (
    // Press-to-rename: the chip IS the trigger, exactly as in the list band,
    // so the press target is the label it renames.
    <Tooltip>
      <TooltipTrigger
        render={
          <StageGroupChip
            stage={stage}
            className="-ml-2"
            render={
              <Box
                as="button"
                aria-label={`Rename ${stage.name}`}
                onClick={() => setEditing(true)}
              />
            }
          />
        }
      />
      <TooltipPopup side="top">Edit stage name</TooltipPopup>
    </Tooltip>
  ) : (
    <StageGroupChip stage={stage} className="-ml-2" />
  );

  return (
    // 8px chip↔count rhythm, the same across every grouping dimension so
    // switching the dimension doesn't reshuffle the header layout. The count
    // sits outside the press target — it reads the column, it doesn't rename it.
    // The 8px lead-in, on top of the header's own 8px inset, is the 16px glyph
    // line shared with the "+ Add Project" footer and the generic dimension
    // header (issues-board renderColumnHeader).
    <Box direction="row" align="center" gap="sm" paddingLeft="sm" shrink>
      {chip}
      <Text size="sm" weight="medium" variant="muted">
        {count.toLocaleString()}
      </Text>
    </Box>
  );
}
